import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Factory, Building2, HardHat, Tractor, Gauge, Warehouse } from 'lucide-react';

const industries = [
  {
    icon: Factory,
    title: 'Manufacturing',
    description: 'Supply chain logistics for raw materials, components, and finished goods. Just-in-time delivery solutions.',
  },
  {
    icon: HardHat,
    title: 'Construction',
    description: 'Heavy equipment, building materials, and construction machinery transport. Oversized load specialists.',
  },
  {
    icon: Tractor,
    title: 'Agriculture',
    description: 'Farm equipment, machinery, and agricultural products. Seasonal and time-sensitive freight solutions.',
  },
  {
    icon: Gauge,
    title: 'Oil & Gas',
    description: 'Oilfield equipment, pipeline materials, and industrial freight. Permitted loads and specialized handling.',
  },
  {
    icon: Building2,
    title: 'Commercial',
    description: 'Retail distribution, commercial equipment, and business logistics. FTL and LTL solutions.',
  },
  {
    icon: Warehouse,
    title: 'Warehousing & Distribution',
    description: 'Last-mile delivery, cross-docking, and inventory management. Nationwide coverage.',
  },
];

export default function IndustriesPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="industries-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase"
          >
            Industries <span className="text-orange-500">We Serve</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-base lg:text-lg text-slate-400 font-body max-w-2xl mx-auto"
          >
            Specialized freight solutions tailored to your industry&apos;s unique requirements.
          </motion.p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 border border-slate-200 hover:border-orange-500/30 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-navy-900 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                  <industry.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 uppercase mb-3">
                  {industry.title}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {industry.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <p className="font-body text-slate-600 mb-6">
              Don&apos;t see your industry? We work with businesses across all sectors.
            </p>
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-body font-bold uppercase tracking-wide px-8 py-6 rounded-sm">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
